/**
 * AnalyticsCardGranularityControl — one chart card's "View by" bucket width (VRG-512)
 *
 * Picks THIS CARD'S bucket: hour / day / week / month / year, plus Automatic. It renders in
 * the card shell's header `action` slot, immediately left of the line/bar mark toggle,
 * because it answers the same class of question: how is this one series drawn.
 *
 * ⚠️ IT USED TO BE A PAGE-LEVEL TOOLBAR CONTROL, and the move is the fix rather than a
 * relocation. See `analyticsCardGranularity`'s header for the measurement (one "View by"
 * change re-bucketed every chart card on the canvas) and for why the bucket is a display
 * property rather than a data property. Two variants — a 49px toolbar control and a
 * chip-shaped fallback below a 1904px container query — went with it; both are deleted,
 * along with `TOOLBAR_GRANULARITY_MIN_WIDTH_PX` and its inverse. A per-card control has no
 * responsive stage to fall out of, which is the collapse of a whole mechanism, not a tidy.
 *
 * ## Why a granularity control is not the smoothing control that was rejected
 *
 * This epic explicitly declined to add a "smoothing" setting, and it is worth writing down
 * why this one is different rather than leaving the two to look like the same concession.
 * A smoothing knob alters the DRAWING of a series while the underlying numbers stay put —
 * it lets a chart misrepresent its data on request, and there is no honest reading of
 * "show me this less accurately". Granularity changes the QUESTION: "scans per day" and
 * "scans per month" are two different, equally true measurements, and the marks continue
 * to sum to the same total either way. Every bucket is computed server-side
 * (`analyticsBucketRequest`), so switching unit re-asks the API rather than re-drawing a
 * cached answer.
 *
 * ## Automatic first, override second — and the order matters
 *
 * The chart already picks a sensible unit for the range on its own, so a reader never has
 * to touch this to get a readable chart; the control exists for the reader whose question
 * is not the one the range implies. That is why `Automatic` is a real, selectable row and
 * the stored value is `null` rather than a pre-filled unit — see `analyticsCardGranularity`
 * for why storing the derived unit would quietly turn everyone into a permanent overrider.
 *
 * ## Unavailable units are DISABLED WITH A REASON, never hidden (§46)
 *
 * A control whose rows appear and vanish as the date picker moves reads as a bug, and it
 * leaves the reader unsure whether they imagined an option. So every unit is always
 * listed, and one that cannot work for the current range carries a tooltip naming the fix
 * ("Select a longer range to view by year") rather than a restatement of its state. The
 * bounds and their justification live on `ANALYTICS_MIN_BUCKETS` / `ANALYTICS_MAX_BUCKETS`.
 *
 * ## The fallback has to be VISIBLE, which is most of this component's job
 *
 * An override survives a range change rather than snapping back, so a reader who picks
 * Year and then narrows to one month has a preference the range cannot honour.
 * `analyticsEffectiveBucketUnit` silently falls back, and silence there would be the
 * re-derivation §46 warns about — the chart would be drawn at a unit nobody asked for with
 * nothing to say so. This component is what closes that: the trigger reads
 * `Year (showing days)`, and the chosen row stays CHECKED while disabled, so the preference
 * is visibly still held rather than looking discarded.
 *
 * ## The trigger is CARD FURNITURE, sized for the crowded case
 *
 * 🚨 The layout constraint is NOT the simple card — that has ~645px of header to spare at
 * 1280. It is the COMPOSED card, whose `action` slot already stacks the status legend, a
 * per-series remove chip for every composed metric (unbounded — one per drop), and the mark
 * toggle. So this borrows `ViewToggle`'s weight rather than the toolbar control's: 40px to
 * match its neighbours in that row (§29 — the header band is 40, not the toolbar's 49),
 * nothing painted at rest, `text.secondary`, hover/open filling in. And it renders the UNIT
 * ALONE ("Month") rather than the toolbar's "View by: Month", which is ~74px narrower — the
 * qualifier moves into the tooltip and the accessible name, where it costs no width. In the
 * card, the neighbouring chart supplies the context that the qualifier supplied in a row of
 * unrelated filters.
 *
 * It does NOT shrink below that: `flexShrink: 0`, and the composed builder gives the legend
 * group `minWidth: 0` so the variable-width thing yields to the fixed-width ones. A control
 * that truncated to "Mon…" would be §49's defect one layer down.
 */
import React, { useMemo } from 'react'

import { CalendarRangeIcon, CheckIcon, WandSparklesIcon } from 'lucide-react'
import { Box, Stack, Tooltip, Typography, useTheme } from '@mui/material'

import { ActionDropdown, type ActionDropdownItem } from 'components/input/ActionDropdown'
import { CONTROL_HEIGHT } from '@repo/ui'

import { useAnalyticsCardGranularity } from './analyticsCardGranularity'

import {
  ANALYTICS_BUCKET_UNIT_LABELS,
  analyticsBucketOptions,
  analyticsChartGranularity,
  analyticsEffectiveBucketUnit,
} from './analyticsDisplay'

interface AnalyticsCardGranularityControlProps {
  /**
   * The card's own identity (`analyticsVariantCardId(...)`) — the store key.
   *
   * Must be the SAME id passed to the `ScansByTime2` this sits above and to that card's
   * `ScansByTimeMarkToggle`, or the header would change a bucket the body never reads.
   * Required rather than optional so a missing id is a type error rather than a card that
   * quietly shares state with every other unkeyed card — the same rule, for the same
   * reason, as `ScansByTimeMarkToggle`'s own `cardId`.
   */
  cardId: string
  /** The range the options are gated against. The page's `filters.dateRange`. */
  from: Date
  to: Date
}

export const AnalyticsCardGranularityControl = ({
  cardId,
  from,
  to,
}: AnalyticsCardGranularityControlProps) => {
  const theme = useTheme()
  const [value, onChange] = useAnalyticsCardGranularity(cardId)

  const options = useMemo(() => analyticsBucketOptions(from, to), [from, to])
  const auto = useMemo(() => analyticsChartGranularity(from, to), [from, to])
  const effective = useMemo(
    () => analyticsEffectiveBucketUnit(from, to, value),
    [from, to, value],
  )

  /*
    The override is held but not honoured — the one state the trigger has to narrate.
    `value && effective !== value` is exactly "you asked for X and you are seeing Y";
    when `value` is null there is no preference to contradict.
  */
  const overridden = Boolean(value) && effective !== value

  const valueText = value
    ? overridden
      ? `${ANALYTICS_BUCKET_UNIT_LABELS[value]} (showing ${ANALYTICS_BUCKET_UNIT_LABELS[
          effective
        ].toLowerCase()}s)`
      : ANALYTICS_BUCKET_UNIT_LABELS[value]
    : ANALYTICS_BUCKET_UNIT_LABELS[effective]

  /*
    The QUALIFIER lives in the tooltip and the accessible name, not in the trigger — see the
    header's note on sizing. A screen reader still hears "View by: Month", so nothing about
    the control's meaning depends on the visual context that let the words be dropped.
  */
  const triggerLabel = `View by: ${valueText}`

  const items: ActionDropdownItem[] = [
    {
      label: `Automatic (${ANALYTICS_BUCKET_UNIT_LABELS[auto].toLowerCase()})`,
      // Not a unit glyph — Automatic is a MODE, and giving it a calendar icon like the
      // units below would read as a sixth bucket width rather than as "you decide".
      icon: <WandSparklesIcon size={16} strokeWidth={1} />,
      onClick: () => onChange(null),
      ...(value === null ? { hint: <CheckIcon size={14} strokeWidth={1.5} /> } : {}),
      divider: true,
    },
    ...options.map(({ unit, label, enabled, reason }) => ({
      label,
      icon: <CalendarRangeIcon size={16} strokeWidth={1} />,
      disabled: !enabled,
      // §46's forcing function: a disabled row MUST say why. `reason` is present iff the
      // option is disabled, which is asserted by `AnalyticsBucketOption`'s own shape.
      ...(enabled ? {} : { tooltip: reason }),
      /*
        The check follows the READER'S CHOICE, not the effective unit.

        A disabled row can still be checked, and that pairing is the point: it is what says
        "this is still your preference, the current range just cannot honour it". Checking
        the effective unit instead would show the fallback as though the reader had picked
        it, and their actual choice would look silently discarded.
      */
      ...(value === unit ? { hint: <CheckIcon size={14} strokeWidth={1.5} /> } : {}),
      onClick: () => onChange(unit),
    })),
  ]

  return (
    <ActionDropdown
      items={items}
      trigger={({ onClick, open }) => (
        <Tooltip title={triggerLabel}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            onClick={onClick}
            role="button"
            aria-haspopup="menu"
            aria-expanded={open}
            aria-label={triggerLabel}
            tabIndex={0}
            sx={{
              /*
                40 — the CARD HEADER band, which is `ViewToggle`'s and the shell's own
                grip / expand / remove buttons'. Not the toolbar's 49: that height exists
                to match a 49px search FIELD sitting beside it (§29), and there is no field
                in this row. A 49px control here would stand 9px taller than every
                neighbour it shares the slot with.
              */
              height: CONTROL_HEIGHT,
              px: 2,
              // The variable-width legends beside this yield first; see the header.
              flexShrink: 0,
              cursor: 'pointer',
              borderRadius: 2,
              whiteSpace: 'nowrap',
              userSelect: 'none',
              // Card furniture: nothing painted at rest, so a chart header reads as a
              // title and a plot rather than as a title and a row of boxed controls.
              color: open ? 'text.primary' : 'text.secondary',
              backgroundColor: open ? 'action.hover' : 'transparent',
              transition: 'color 120ms ease, background-color 120ms ease',
              '&:hover': { color: 'text.primary', backgroundColor: 'action.hover' },
              // The only focus affordance there is, since nothing is drawn at rest — the
              // same ring `ViewToggle` uses, for the same reason.
              '&:focus-visible': {
                outline: `2px solid ${theme.palette.brand[600]}`,
                outlineOffset: 1,
              },
            }}
          >
            <Box sx={{ display: 'flex' }}>
              <CalendarRangeIcon size={16} strokeWidth={1} />
            </Box>
            <Typography variant="body3" sx={{ color: 'inherit' }}>
              {valueText}
            </Typography>
          </Stack>
        </Tooltip>
      )}
    />
  )
}

export default AnalyticsCardGranularityControl
